import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { FiBarChart2 } from "react-icons/fi";
import CrudDetailPage, { DetailSection, DetailField, DetailGrid } from "../../../components/crud/CrudDetailPage";
import FormGrid, { FormField } from "../../../components/crud/FormGrid";
import { formatCurrency } from "../../../components/crud/DataTable";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";

const NetWorthEdit = () => {
	const [item, setItem] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	// Edit form state
	const [formData, setFormData] = useState({
		date: new Date().toISOString().split("T")[0],
		value: "",
		note: "",
		account_values: "",
	});

	const location = useLocation();
	const id = location.pathname.split("/")[2];
	const isNew = id === "new";

	useEffect(() => {
		const fetchData = async () => {
			try {
				if (!isNew) {
					const res = await axios.get(`${backendUrl}/networth/entries/${id}`);
					const data = res.data;
					setItem(data);

					setFormData({
						date: data.date ? data.date.split("T")[0] : "",
						value: data.value || "",
						note: data.note || "",
						account_values: data.account_values || "",
					});
				}
			} catch (err) {
				console.error("Error fetching net worth entry:", err);
			} finally {
				setLoading(false);
			}
		};
		fetchData();
	}, [id, isNew]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			const saveData = {
				date: formData.date,
				value: parseFloat(formData.value) || 0,
				note: formData.note || null,
				account_values: formData.account_values || null,
			};

			if (isNew) {
				const res = await axios.post(`${backendUrl}/networth/entries`, saveData);
				window.location.href = `/networth/${res.data.id}`;
			} else {
				await axios.put(`${backendUrl}/networth/entries/${id}`, saveData);
				const res = await axios.get(`${backendUrl}/networth/entries/${id}`);
				setItem(res.data);
			}
		} catch (err) {
			console.error("Error saving net worth entry:", err);
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return <div style={{ padding: "20px" }}>Loading...</div>;
	}

	// Edit form content
	const editForm = (
		<FormGrid>
			<FormField label="Date" name="date" width="half" required>
				<input
					type="date"
					name="date"
					value={formData.date}
					onChange={handleChange}
					required
				/>
			</FormField>

			<FormField label="Net Worth Value ($)" name="value" width="half" required>
				<input
					type="number"
					name="value"
					value={formData.value}
					onChange={handleChange}
					step="0.01"
					placeholder="e.g., 50000.00"
					required
				/>
			</FormField>

			<FormField label="Note" name="note" width="full">
				<textarea
					name="note"
					value={formData.note}
					onChange={handleChange}
					placeholder="Optional note about this entry..."
					rows={3}
				/>
			</FormField>

			<FormField label="Account Values (JSON)" name="account_values" width="full">
				<textarea
					name="account_values"
					value={formData.account_values}
					onChange={handleChange}
					placeholder='Optional JSON for account breakdown, e.g., {"checking": 5000, "savings": 10000}'
					rows={4}
					style={{ fontFamily: "monospace", fontSize: "0.9rem" }}
				/>
			</FormField>
		</FormGrid>
	);

	// For new items, show just the form
	if (isNew) {
		return (
			<CrudDetailPage
				title="New Net Worth Entry"
				backLink="/networth"
				backLabel="All Entries"
				breadcrumb={[
					{ label: "Money", to: "/money" },
					{ label: "Net Worth", to: "/networth" },
					{ label: "New" },
				]}
				editForm={editForm}
				onSave={handleSave}
				isLoading={saving}
				variant="v4"
				icon={<FiBarChart2 />}
			/>
		);
	}

	if (!item) {
		return <div style={{ padding: "20px" }}>Net worth entry not found</div>;
	}

	const numValue = parseFloat(item.value);
	const isPositive = numValue >= 0;

	// Detail view content
	const detailContent = (
		<>
			<DetailSection title="Entry Details">
				<DetailGrid>
					<DetailField
						label="Date"
						value={new Date(item.date).toLocaleDateString("en-US", {
							weekday: "long",
							month: "long",
							day: "numeric",
							year: "numeric",
						})}
					/>
					<DetailField
						label="Net Worth"
						value={
							<span style={{ color: isPositive ? "#2ecc71" : "#e74c3c", fontWeight: 600, fontSize: "1.5rem" }}>
								{formatCurrency(numValue)}
							</span>
						}
					/>
				</DetailGrid>
			</DetailSection>

			{item.note && (
				<DetailSection title="Note">
					<p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{item.note}</p>
				</DetailSection>
			)}

			{item.account_values && (
				<DetailSection title="Account Breakdown">
					<pre style={{ margin: 0, fontSize: "0.9rem", background: "#f5f5f5", padding: "1rem", borderRadius: "4px", overflow: "auto" }}>
						{typeof item.account_values === "string"
							? JSON.stringify(JSON.parse(item.account_values), null, 2)
							: JSON.stringify(item.account_values, null, 2)}
					</pre>
				</DetailSection>
			)}
		</>
	);

	const formattedDate = new Date(item.date).toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});

	return (
		<CrudDetailPage
			title={`Net Worth: ${formattedDate}`}
			backLink="/networth"
			backLabel="All Entries"
			breadcrumb={[
				{ label: "Money", to: "/money" },
				{ label: "Net Worth", to: "/networth" },
				{ label: formattedDate },
			]}
			detailContent={detailContent}
			editForm={editForm}
			onSave={handleSave}
			isLoading={saving}
			variant="v4"
			icon={<FiBarChart2 />}
		/>
	);
};

export default NetWorthEdit;
